import { AfterViewInit, Component, HostBinding, Input, OnDestroy, Optional, Self } from '@angular/core';
import { catchError, Observable, of, OperatorFunction, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { DeviceDetectorService } from 'ngx-device-detector';
import { isControlValid } from '../utils';

@Component({
  selector: 'mui-autocomplete',
  templateUrl: './mui-autocomplete.component.html',
  styleUrls: ['./mui-autocomplete.component.scss']
})
export class MuiAutocomplete implements ControlValueAccessor, OnDestroy, AfterViewInit {
  @Input() data: Observable<string[]> = of([]);
  @Input() label = '';
  @Input() name = '';
  @Input() mobileList = 5;
  @Input() desktopList = 15;
  @Input() searchStart = 2;
  searchData: string[] = [];
  private _loading = true;
  private _focus = false;
  private _unsubscribe$ = new Subject<void>();
  private _currentValue = '';
  private readonly _isMobile: boolean;

  constructor(private deviceDetectorService: DeviceDetectorService, @Self() @Optional() protected _control: NgControl) {
    if (_control) {
      _control.valueAccessor = this;
    }
    this._isMobile = this.deviceDetectorService.isMobile() || this.deviceDetectorService.isTablet();
  }

  ngAfterViewInit(): void {
    this._control.control?.updateValueAndValidity();
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  get value(): string {
    return this._currentValue;
  }

  set value(newValue: string) {
    this._currentValue = newValue;
  }

  get valid(): boolean {
    return this._control ? isControlValid(this._control) : true;
  }

  @HostBinding('class.is--mobile')
  get isMobile(): boolean {
    return this._isMobile;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  propagateChange = (_: string): void => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  propagateTouch = (): void => {};

  registerOnChange(fn: (_: string) => void): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.propagateTouch = fn;
  }

  search: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map((term) =>
        term.length < this.searchStart
          ? []
          : this.searchData
              .filter((v) => v.toLowerCase().indexOf(term.toLowerCase()) > -1)
              .slice(0, this._isMobile ? this.mobileList : this.desktopList)
      )
    );

  handleOnFocus(e: Event): void {
    this._focus = true;
    this.getSearchData(e);
  }

  getSearchData(e: Event): void {
    if (!this.searchData?.length) {
      this.data
        .pipe(
          takeUntil(this._unsubscribe$),
          catchError(() => {
            this._loading = false;
            return of([]);
          })
        )
        .subscribe({
          next: (data: string[]) => {
            this.searchData = data;
            this._loading = false;
          }
        });
    } else {
      e.stopPropagation();
      setTimeout(() => {
        const inputEvent: Event = new Event('input');
        e.target?.dispatchEvent(inputEvent);
      }, 0);
    }
  }

  handleOnBlur(): void {
    this._focus = false;
    this._loading = false;
  }

  handleDisplayLoading(): boolean {
    return this._loading && this._focus;
  }

  writeValue(value: string): void {
    this.value = value;
  }

  onChange(): void {
    this._control.control?.updateValueAndValidity();
    this.propagateChange(this.value);
    this.propagateTouch();
  }
}
